import { encodeBase32 } from "../../common/base32";
import type { Algorithm } from "./hotp";
import { readSecret, type SecretFormat } from "./secret";
import { COUNTER_RANGE, DIGIT_RANGE, type Mode, parseWhole, PERIOD_RANGE, pickAlgorithm, type Range } from "./settings";

// The Key Uri Format an authenticator is enrolled with, which has a type for a counter and a clock and none for a
// challenge, so this is TOTP's and HOTP's and OCRA has no URI at all. The fields and the one line are the same
// state read twice: `writeUri` spells them and `readUri` reads them back, and the page holds no third copy.
export type UriMode = Exclude<Mode, "ocra">;

export interface UriFields {
  mode: UriMode;
  issuer: string;
  label: string;
  secret: string;
  format: SecretFormat;
  algorithm: Algorithm;
  // Null is a box that does not read as a number yet, and a parameter left out rather than written from a guess.
  digits: number | null;
  period: number | null;
  counter: number | null;
}

// What a page nobody has typed into enrols: this site, and the one tab the secret it holds never leaves.
export const DEFAULT_ISSUER = "utils.plus";
export const DEFAULT_LABEL = "local";

export function writeUri(fields: UriFields): string {
  const { mode, issuer, label, algorithm, digits, period, counter } = fields;
  // The colon is written whenever there is an issuer, since a prefix with nothing after it is still a prefix and a
  // path with no colon in it at all is a label — `Acme` alone reads back as the account and not as who it is with.
  const path = issuer ? `${encodePart(issuer)}:${encodePart(label)}` : encodePart(label);
  const query = [];

  const secret = base32Secret(fields.secret, fields.format);
  if (secret) query.push(param("secret", secret));
  // Both spellings of the issuer, as the format recommends: the prefix is what older readers know it by.
  if (issuer) query.push(param("issuer", issuer));
  // Written even where it is the format's own default, a parameter nobody can see being one nobody can edit.
  query.push(param("algorithm", algorithm));
  if (digits !== null) query.push(param("digits", String(digits)));
  if (mode === "hotp") {
    if (counter !== null) query.push(param("counter", String(counter)));
  } else if (period !== null) query.push(param("period", String(period)));

  return `otpauth://${mode}/${path}?${query.join("&")}`;
}

// Lenient in the ways a URI is edited in place, since half a parameter is one that is not there yet. What it
// answers with is the whole of the page's fields and never a merge with them: a parameter the format gives a
// default to is that default when it is absent, which is what makes pasting somebody's URI the page it describes
// rather than a mix of it and whatever the boxes were holding. Null is text that is not one of these URIs at all.
export function readUri(text: string, current: UriFields): UriFields | null {
  const match = URI.exec(text.trim());
  if (!match) return null;
  const mode = match[1].toLowerCase() as UriMode;
  const params = readQuery(match[3] ?? "");
  const { issuer: prefix, label } = splitLabel(match[2] ?? "");
  const secret = params.get("secret") ?? "";
  // The URI spells a secret in Base32 and the box may be showing the same bytes as hex or as words, so the two are
  // compared as bytes and the box is rewritten only where they differ; an edit anywhere else in the URI leaves the
  // format somebody chose alone, and an unreadable secret is different from every readable one and so is written.
  const kept = base32Secret(secret, "base32") === base32Secret(current.secret, current.format);

  return {
    mode,
    issuer: readIssuer(prefix, params.get("issuer"), current.issuer),
    label,
    secret: kept ? current.secret : secret,
    format: kept ? current.format : "base32",
    algorithm: pickAlgorithm(params.get("algorithm")?.toUpperCase().replace(/-/g, "")),
    digits: whole(params.get("digits"), DIGIT_RANGE) ?? 6,
    // The parameter the other type does not carry is one this URI says nothing about, so its box keeps what it had.
    period: mode === "totp" ? whole(params.get("period"), PERIOD_RANGE) ?? 30 : current.period,
    counter: mode === "hotp" ? whole(params.get("counter"), COUNTER_RANGE) ?? 0 : current.counter,
  };
}

// Whether a URI carries a key at all, asked of the text on screen rather than of the fields behind it, since what
// is scanned is what the box is showing. One that carries none enrols an account with nothing behind it, and text
// that is not one of these URIs carries none either.
export function uriKeyless(text: string): boolean {
  return !readQuery(URI.exec(text.trim())?.[3] ?? "").get("secret");
}

// The issuer is written twice and can therefore be edited in either place, so the one that has moved off what the
// page is holding is the one somebody just typed. The prefix wins where both have moved, being the half a reader
// sees first; and a path with no prefix at all leaves the parameter answering alone, which is how a URI carrying
// only the modern spelling is read without the missing prefix reading as an issuer somebody deleted.
function readIssuer(prefix: string | null, param: string | undefined, current: string): string {
  if (prefix === null) return param ?? "";
  if (prefix !== current) return prefix;
  return param ?? prefix;
}

// Null is a path with no separator in it, which is not a path whose prefix is empty: the first says the parameter
// is the only thing claiming an issuer, and the second that somebody took the prefix out.
function splitLabel(path: string): { issuer: string | null; label: string } {
  const separator = /:|%3A/i.exec(path);
  if (!separator) return { issuer: null, label: decodePart(path) };
  return {
    issuer: decodePart(path.slice(0, separator.index)),
    label: decodePart(path.slice(separator.index + separator[0].length)),
  };
}

// Standard URI escaping and not a form's: `%20` is the space in a Key Uri Format label and a `+` in one is a plus,
// where `URLSearchParams` reads and writes it the other way round, which is the whole of why neither half uses it.
function readQuery(query: string): Map<string, string> {
  const params = new Map<string, string>();
  for (const pair of query.split("&")) {
    if (!pair) continue;
    const split = pair.indexOf("=");
    const name = split === -1 ? pair : pair.slice(0, split);
    params.set(decodePart(name).toLowerCase(), split === -1 ? "" : decodePart(pair.slice(split + 1)));
  }
  return params;
}

function param(name: string, value: string): string {
  return `${name}=${encodeURIComponent(value)}`;
}

// `@` is left as it stands: an account name is usually an email address, the character is legal in a path segment,
// and it is bare in the URIs the format's own examples print.
function encodePart(text: string): string {
  return encodeURIComponent(text).replace(/%40/g, "@");
}

// A `%` that opens nothing is a character somebody typed rather than an escape, so the text stands as it is.
function decodePart(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

function whole(text: string | undefined, range: Range): number | null {
  return text === undefined || text === "" ? null : parseWhole(text, range);
}

// The one spelling the format carries, whatever the box is showing. A secret that will not read has no bytes to
// spell, so the parameter is left out rather than written wrong.
function base32Secret(secret: string, format: SecretFormat): string {
  if (!secret) return "";
  try {
    return encodeBase32(readSecret(secret, format));
  } catch {
    return "";
  }
}

// Anchored at both ends, so a scheme or a type that only opens like one of these is not read as one.
const URI = /^otpauth:\/\/(totp|hotp)(?:\/([^?#]*))?(?:\?([^#]*))?(?:#.*)?$/i;
